import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { map, type Observable } from 'rxjs';
import {
  ConfirmDialogComponent,
  type ConfirmDialogData,
} from '../shared/components/mind-confirm-dialog/mind-confirm-dialog.component';
import AddMarkerDialogComponent from '../shared/components/add-marker-dialog/add-marker-dialog.component';
import type { AddMarkerDialogData } from '../features/staff-monitor/model/add-marker-dialog-data';
import type { ComponentType } from '@angular/cdk/portal';
import { RecordingExportDialogComponent } from '../shared/components/recording-export-dialog/recording-export-dialog.component';
import type { SessionRecordingData } from '../features/session/model/session-recording-data';
import { ConfigureParticipantDialogComponent } from '../shared/components/configure-participant-dialog/configure-participant-dialog.component';
import type { ConfigureParticipantDialogData } from '../shared/components/configure-participant-dialog/configure-participant-dialog-data';
import type { ParticipantTrendDialogData } from '../features/staff-monitor/participant-trend-dialog/participant-trend-dialog-data';
import ParticipantTrendDialogComponent from '../features/staff-monitor/participant-trend-dialog/participant-trend-dialog.component';
import { UpdateAvailableDialogComponent } from '../shared/components/update-available-dialog/update-available-dialog.component';
import type { DeleteMarkerDialogData } from '../shared/components/delete-marker-dialog/delete-marker-dialog-data';
import { DeleteMarkerDialogComponent } from '../shared/components/delete-marker-dialog/delete-marker-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class DialogService {
  private readonly dialog = inject(MatDialog);

  confirmDialog(data: ConfirmDialogData): Observable<boolean> {
    return this.openDialog<ConfirmDialogComponent, ConfirmDialogData>(ConfirmDialogComponent, data);
  }

  addMarkerDialog(data: AddMarkerDialogData): Observable<boolean> {
    return this.openDialog<AddMarkerDialogComponent, AddMarkerDialogData>(AddMarkerDialogComponent, data);
  }

  openTrendDialog(data: ParticipantTrendDialogData): Observable<boolean> {
    return this.openDialog<ParticipantTrendDialogComponent, ParticipantTrendDialogData>(
      ParticipantTrendDialogComponent,
      data,
      '90%',
    );
  }

  /** @return Azonnali frissítés esetén -> true */
  openUpdateAvailableDialog(allowDelay: boolean): Observable<boolean> {
    return this.openDialog<UpdateAvailableDialogComponent, boolean>(UpdateAvailableDialogComponent, allowDelay);
  }

  recordingExportDialog(data: SessionRecordingData): Observable<boolean> {
    return this.openDialog<RecordingExportDialogComponent, SessionRecordingData>(RecordingExportDialogComponent, data);
  }

  configureParticipantDialog(data: ConfigureParticipantDialogData): Observable<boolean> {
    return this.openDialog<ConfigureParticipantDialogComponent, ConfigureParticipantDialogData>(
      ConfigureParticipantDialogComponent,
      data,
    );
  }

  deleteMarkerDialog(data: DeleteMarkerDialogData): Observable<boolean> {
    return this.openDialog<DeleteMarkerDialogComponent, DeleteMarkerDialogData>(
      DeleteMarkerDialogComponent,
      data,
      '480px',
    );
  }

  private openDialog<C, D>(component: ComponentType<C>, data: D, width = '400px'): Observable<boolean> {
    return this.dialog
      .open<C, D, boolean>(component, {
        width,
        maxWidth: '100%',
        disableClose: true,
        data,
      })
      .afterClosed()
      .pipe(map(result => result ?? false));
  }
}
